import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'
import * as yup from 'yup'
import { useHistory } from 'react-router-dom'
import { t } from 'translation'
import { Link } from 'react-router-dom'

import CheckboxItem from 'components/CheckboxItem/CheckboxItem'
import CheckboxItemV2 from 'components/CheckboxItem/CheckboxItemV2'
import LoadingButton from 'components/ui/LoadingButton/LoadingButton'
import AuthErrorBox from 'components/Auth/AuthErrorBox'

import { accountsApi } from 'api'
import { connect } from 'react-redux'
import { authFail, resetAuthError, setOwnInitialPassword } from 'store/actions'

import BgLoginImg from 'assets/woman.jpg'
import LogoLoginImg from 'assets/logo_tr.png'

const RegisterDoctorBody = function RegisterDoctorBody({
  authFail,
  authError,
  inviteDoctor,
  resetAuthError,
  setOwnInitialPassword,
}) {
  useEffect(() => {
    document.documentElement.classList.add('no_scale_mod')
  }, [])

  const history = useHistory()

  const [success, setSuccess] = useState(false)
  const [showPasswords, setShowPasswords] = useState(false)

  const title = inviteDoctor?.eLearningAccessAt
    ? t('registrationPage.CONGRATULATIONS')
    : t('registrationPage.DOCTOR_REGISTRATION')

  const doctorRegisterSchema = useMemo(
    () =>
      yup.object().shape({
        email: yup.string().email(),
        name: yup
          .string()
          .required(t('registrationPage.NAME_IS_REQUIRED'))
          .max(162, t('registrationPage.NAME_CAN_BE_AT_MOST', { max: 162 })),
        doctorId: yup
          .string()
          .required(t('registrationPage.DOCTOR_ID_IS_REQUIRED'))
          .max(256, t('registrationPage.DOCTOR_ID_CAN_BE_AT_MOST', { max: 256 }))
          .matches(/^[0-9]{9,}$/, t('registrationPage.RPPS_invalid')),
        password: yup
          .string()
          .required(t('registrationPage.PASSWORD_IS_REQUIRED'))
          .min(6, t('registrationPage.PASSWORD_IS_TOO_SHORT'))
          .max(256, t('registrationPage.PASSWORD_CAN_BE_AT_MOST', { max: 256 }))
          .matches(
            /^.*(?=.{6,})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
            t('registrationPage.PASSWORD_MUST_CONTAIN')
          ),
        passwordConfirm: yup
          .string()
          .required(t('registrationPage.PASSWORD_IS_REQUIRED'))
          .oneOf([yup.ref('password')], t('registrationPage.PASSWORDS_DO_NOT_MATCH')),
      }),
    []
  )

  return (
    <>
      <section className="section login_mod">
        <div className="section_bg doctor_mod">
          <img className="section_bg_img" src={BgLoginImg} alt="background" />
        </div>
        <div className="section_in login_mod">
          <div className="logo doctor_mod">
            <img
              className="logo_img"
              src={LogoLoginImg}
              align="center"
              alt="Logo Sound Care"
            />
          </div>
          <div className="tab_content_wrap auth_block">
            <Formik
              initialValues={{
                email: inviteDoctor?.email || '',
                name: '',
                password: '',
                passwordConfirm: '',
                doctorId: '',
                termsAgree: false,
                termsAgree2: false,
              }}
              onSubmit={async (
                { name, password, doctorId, termsAgree, termsAgree2 },
                { setSubmitting }
              ) => {
                resetAuthError()
                if (!termsAgree) return authFail('JOIN_TERMS_UNCHECKED')
                if (!termsAgree2) return authFail('JOIN_TERMS_UNCHECKED')
                try {
                  setSubmitting(true)
                  resetAuthError()
                  const { result, error } = await accountsApi.createDoctorAccount({
                    doctorId,
                    name,
                    inviteDoctorId: inviteDoctor.id,
                  })

                  if (error) throw Error(error?.message)

                  const { code } = result

                  const passwordSetSuccess = await setOwnInitialPassword({
                    email: inviteDoctor.email,
                    tempPassword: code,
                    ownPassword: password,
                  })

                  setSubmitting(false)
                  if (!passwordSetSuccess) throw Error('ERROR')
                  setSuccess(true)
                  // setSnackbar(true, tSnackbar('REGISTER_DOCTOR_SNACKBAR_SUCCESS'))
                  setTimeout(() => history.push('/welcome-doctor'), 4000)
                } catch (error) {
                  console.log(
                    'Error creating doctor accoount: ',
                    error?.error_description || error?.message || error
                  )
                  setSubmitting(false)
                  return authFail(error)
                }
              }}
              validationSchema={doctorRegisterSchema}
            >
              {({
                errors,
                touched,
                setFieldValue,
                submitForm,
                isSubmitting,
                status,
                setStatus,
                handleChange,
              }) => (
                <Form className="tab_content login_mod active_tab contact_form">
                  <h2 className="section_title v3_mod center_level_mod color_mod offset_v3_mod">
                    {title}
                  </h2>
                  <div className="login_descr_text v4_mod">
                    {t('registrationPage.CONGRATULATIONS_SUBTITLE')}
                  </div>

                  <div className="form_controller doctor_mod">
                    <dl className="form_cell form_cell_v1_mod">
                      <dt className="form_cell_title form_v3_mod">
                        <label htmlFor="register_email">
                          {t('registrationPage.EMAIL')}
                        </label>
                      </dt>
                      <dd className="form_field_wrap form_v3_mod">
                        <Field
                          className={`form_field v4_mod ${
                            errors.email && touched.email ? 'error' : ''
                          }`}
                          type="text"
                          id="email"
                          name="email"
                          placeholder={t('registrationPage.EMAIL')}
                          disabled={true}
                          autoComplete="off"
                        />
                      </dd>
                    </dl>
                  </div>
                  <div className="form_controller doctor_mod">
                    <dl className="form_cell form_cell_v1_mod">
                      <dt className="form_cell_title form_v3_mod">
                        <label htmlFor="register_name">
                          {t('registrationPage.NAME')}
                        </label>
                      </dt>
                      <dd className="form_field_wrap form_v3_mod">
                        <Field
                          className={`form_field v4_mod ${
                            errors.name && touched.name ? 'error' : ''
                          }`}
                          id="name"
                          name="name"
                          placeholder=" "
                          type="text"
                          required
                          onChange={(e) => (resetAuthError(), handleChange(e))}
                          disabled={!inviteDoctor || isSubmitting}
                          autoFocus
                        />
                      </dd>
                    </dl>
                  </div>
                  <div className="form_controller doctor_mod">
                    <dl className="form_cell form_cell_v1_mod">
                      <dt className="form_cell_title form_v3_mod">
                        <label htmlFor="register_doctorId">
                          {t('registrationPage.DOCTOR_ID')}
                        </label>
                      </dt>
                      <dd className="form_field_wrap form_v3_mod">
                        <Field
                          className={`form_field v4_mod ${
                            errors.doctorId && touched.doctorId ? 'error' : ''
                          }`}
                          id="doctor-id"
                          name="doctorId"
                          placeholder=" "
                          type="text"
                          required
                          disabled={!inviteDoctor || isSubmitting}
                          onChange={({ target: { value } }) =>
                            setFieldValue('doctorId', value.trim())
                          }
                        />
                      </dd>
                    </dl>
                  </div>
                  <div className="form_controller doctor_mod">
                    <dl className="form_cell form_cell_v1_mod">
                      <dt className="form_cell_title form_v3_mod">
                        <label htmlFor="register_password">
                          {t('registrationPage.PASSWORD')}
                        </label>
                      </dt>
                      <dd className="form_field_wrap form_v3_mod">
                        <Field
                          className={`form_field v4_mod ${
                            errors.password && touched.password ? 'error' : ''
                          }`}
                          id="password"
                          name="password"
                          type={showPasswords ? 'text' : 'password'}
                          placeholder="++++++"
                          required
                          onChange={(e) => (resetAuthError(), handleChange(e))}
                          disabled={!inviteDoctor || isSubmitting}
                          autoComplete="off"
                        />
                      </dd>
                    </dl>
                  </div>
                  <div className="form_controller doctor_mod">
                    <dl className="form_cell form_cell_v1_mod">
                      <dt className="form_cell_title form_v3_mod">
                        <label htmlFor="register_confirmPassword">
                          {t('registrationPage.PASSWORD_CONFIRM')}
                        </label>
                      </dt>
                      <dd className="form_field_wrap form_v3_mod">
                        <Field
                          className={`form_field v4_mod ${
                            errors.passwordConfirm && touched.passwordConfirm
                              ? 'error'
                              : ''
                          }`}
                          id="password-confirm"
                          name="passwordConfirm"
                          type={showPasswords ? 'text' : 'password'}
                          placeholder="++++++"
                          required
                          onChange={(e) => (resetAuthError(), handleChange(e))}
                          disabled={!inviteDoctor || isSubmitting}
                          autoComplete="off"
                        />
                      </dd>
                    </dl>
                  </div>
                  <div className="form_controller doctor_mod child_mod">
                    <CheckboxItem
                      name="termsAgree"
                      id="terms-agree"
                      label={
                        <>
                          {t(`registrationPage.LEGAL_DOCTOR_A`)}
                          <Link
                            to={{ pathname: t('links.TERMS_DOCTOR') }}
                            target="_blank"
                            rel="noopener"
                          >
                            {t(`registrationPage.LEGAL_DOCTOR_LINK`)}
                          </Link>
                          {t(`registrationPage.LEGAL_DOCTOR_B`)}
                        </>
                      }
                      required
                      disabled={!inviteDoctor || isSubmitting}
                      onChange={(e) => (resetAuthError(), handleChange(e))}
                    />
                  </div>
                  <div className="form_controller doctor_mod child_mod">
                    <CheckboxItem
                      name="termsAgree2"
                      id="terms-agree2"
                      label={
                        <>
                          {t(`registrationPage.LEGAL_DOCTOR_C_1`)}
                          <Link
                            to={{ pathname: t('links.TERMS_DOCTOR_2') }}
                            target="_blank"
                            rel="noopener"
                          >
                            {t(`registrationPage.LEGAL_DOCTOR_LINK_2`)}
                          </Link>
                          {t(`registrationPage.LEGAL_DOCTOR_C_2`)}
                          <Link
                            to={{ pathname: t('links.TERMS_DOCTOR_3') }}
                            target="_blank"
                            rel="noopener"
                          >
                            {t(`registrationPage.LEGAL_DOCTOR_LINK_3`)}
                          </Link>
                          {t(`registrationPage.LEGAL_DOCTOR_C_3`)}
                        </>
                      }
                      required
                      disabled={!inviteDoctor || isSubmitting}
                      onChange={(e) => (resetAuthError(), handleChange(e))}
                    />
                  </div>
                  <div className="form_controller doctor_mod">
                    <p className="checkbox_description register_doctor">
                      {
                        <>
                          {t(`registrationPage.LEGAL_DOCTOR_DESCR_A`)}
                          <Link
                            to={{ pathname: t('links.PRIVACY') }}
                            target="_blank"
                            rel="noopener"
                          >
                            {t(`registrationPage.LEGAL_DOCTOR_DESCR_LINK`)}
                          </Link>
                          {t(`registrationPage.LEGAL_DOCTOR_DESCR_B`)}
                        </>
                      }
                    </p>
                  </div>
                  <div className="login_error_container">
                    {authError && (
                      <AuthErrorBox
                        formFields={[
                          'email',
                          'name',
                          'doctorId',
                          'password',
                          'passwordConfirm',
                        ]}
                        formTouched={touched}
                        formErrors={errors}
                        apiResponseError={authError || ''}
                        resetApiResponseError={resetAuthError}
                        namespace={'registrationPage'}
                      />
                    )}
                  </div>
                  <div className="login_bottom">
                    <LoadingButton
                      className="btn_base_v1 full_mod"
                      id="login_submit"
                      type="submit"
                      onClick={() => (setStatus('clicked'), submitForm())}
                      isLoading={isSubmitting}
                      disabled={!inviteDoctor || success}
                    >
                      {t('registrationPage.REGISTER')}
                    </LoadingButton>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </section>
    </>
  )
}

const mapStateToProps = ({ auth: { error } }) => ({ authError: error })
const willMapDispatchToProps = {
  authFail,
  resetAuthError,
  setOwnInitialPassword,
}

export default connect(mapStateToProps, willMapDispatchToProps)(RegisterDoctorBody)

RegisterDoctorBody.propTypes = {
  authError: PropTypes.string,
  authFail: PropTypes.func.isRequired,
  inviteDoctor: PropTypes.object,
  resetAuthError: PropTypes.func.isRequired,
  setOwnInitialPassword: PropTypes.func.isRequired,
}
